import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { teacherEvalShape } from './shapes';
import {
  RANKED_COLUMNS,
  allFilterResults,
  decidedByLabel,
  liveDate,
  filterLabel,
  finishedTrialsSourceLabel,
  formatScaled,
  shownStatusLabel,
  teacherFailed,
  teacherProfileUrl,
  teacherSentence
} from './constants';

function Scaled({ value, scale }) {
  if (value === null || value === undefined) return '—';
  return (
    <>
      {value}
      <span className="rv2-sub">{formatScaled(value, scale)}</span>
    </>
  );
}

Scaled.propTypes = {
  value: PropTypes.number,
  scale: PropTypes.number.isRequired
};

Scaled.defaultProps = { value: null };

function TeacherCell({ t }) {
  const url = teacherProfileUrl(t);
  return (
    <>
      {url ? (
        <a className="rv2-teacher-link" href={url} target="_blank" rel="noopener noreferrer">
          {t.name}
        </a>
      ) : (
        t.name
      )}
      <span className="rv2-sub">
        id {t.teacher_id}
        {t.gender ? ` · ${t.gender}` : ''}
      </span>
    </>
  );
}

TeacherCell.propTypes = { t: teacherEvalShape.isRequired };

const statusVariant = { shown: 'pass', see_more: 'info', see_all: 'neutral' };

const renderRankedCell = (key, t) => {
  switch (key) {
    case 'teacher':
      return <TeacherCell t={t} />;
    case 'goals':
      return (
        <span title={(t.goals_matched || []).join(', ')}>
          {t.matched_goals}/{t.total_goals}
          {(t.goals_matched || []).length > 0 ? (
            <span className="rv2-sub">{t.goals_matched.join(', ')}</span>
          ) : null}
        </span>
      );
    case 'goal_score_x10':
    case 'avail_score_x10':
      return <Scaled value={t[key]} scale={10} />;
    case 'effective_points_x100':
      return <Scaled value={t[key]} scale={100} />;
    case 'retention_score_x100':
      return (
        <>
          <Scaled value={t.retention_score_x100} scale={100} />
          {t.retention_is_platform_avg ? (
            <span
              className="rv2-badge rv2-badge-warn"
              title="No retention data for this teacher; platform average used"
            >
              AVG
            </span>
          ) : null}
        </>
      );
    case 'days_available':
      return `${t.days_available ?? '—'}/14`;
    case 'repeat_available':
      return (
        <span className={`rv2-badge rv2-badge-${t.repeat_available ? 'pass' : 'neutral'}`}>
          {t.repeat_available ? 'yes' : 'no'}
        </span>
      );
    case 'finished_trials':
      return (
        <>
          {t.finished_trials ?? '—'}
          {t.finished_trials_source ? (
            <span className="rv2-sub" title={`finished_trials_source: ${t.finished_trials_source}`}>
              {finishedTrialsSourceLabel(t.finished_trials_source)}
            </span>
          ) : null}
        </>
      );
    case 'live_date':
      return liveDate(t) ? (
        <>
          {liveDate(t)}
          {t.profile_live_at ? null : (
            <span
              className="rv2-sub"
              title="Profile live at is not set; using the profile created date"
            >
              created
            </span>
          )}
        </>
      ) : (
        '—'
      );
    case 'decided_by':
      return t.decided_by ? (
        <span title={`decided_by: ${t.decided_by}`}>{decidedByLabel(t.decided_by)}</span>
      ) : (
        '—'
      );
    case 'shown_status':
      return (
        <span
          className={`rv2-badge rv2-badge-${statusVariant[t.shown_status] || 'neutral'}`}
          title={`shown_status: ${t.shown_status || ''}`}
        >
          {t.shown_status ? shownStatusLabel(t.shown_status) : '—'}
        </span>
      );
    case 'reason':
      return <span style={{ minWidth: 220, display: 'inline-block' }}>{t.reason}</span>;
    default: {
      const v = t[key];
      return v === null || v === undefined || v === '' ? '—' : String(v);
    }
  }
};

// Consecutive columns sharing a band collapse into one spanning header cell.
const BANDS = RANKED_COLUMNS.reduce((acc, c) => {
  const band = c.band || '';
  const last = acc[acc.length - 1];
  if (last && last.band === band) last.span += 1;
  else acc.push({ band, span: 1, key: c.key });
  return acc;
}, []);

const textColumns = [
  'teacher',
  'goals',
  'decided_by',
  'shown_status',
  'reason',
  'repeat_available'
];

export function RankedTable({ ranked }) {
  return (
    <div className="rv2-card">
      <h3>Ranked ({ranked.length})</h3>
      <div className="rv2-hint" style={{ marginBottom: 6 }}>
        Hover a column header for its formula, or a row for a plain-English summary. Scaled integers
        show the decimal value below.
      </div>
      {ranked.length === 0 ? (
        <div className="rv2-empty">
          No teacher passed Stage 0. Check the pool table below for which filter removed them.
        </div>
      ) : (
        <div className="rv2-table-wrap">
          <table className="table-st">
            <thead>
              <tr>
                {BANDS.map((b) => (
                  <th key={b.key} colSpan={b.span} className="rv2-band">
                    {b.band}
                  </th>
                ))}
              </tr>
              <tr>
                {RANKED_COLUMNS.map((c) => (
                  <th key={c.key} title={`${c.key}: ${c.tip}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ranked.map((t) => (
                <tr
                  key={t.teacher_id}
                  className={t.shown_status === 'shown' ? 'rv2-row-shown' : ''}
                  title={teacherSentence(t)}
                >
                  {RANKED_COLUMNS.map((c) => (
                    <td key={c.key} className={textColumns.includes(c.key) ? '' : 'rv2-num'}>
                      {renderRankedCell(c.key, t)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

RankedTable.propTypes = {
  ranked: PropTypes.arrayOf(teacherEvalShape).isRequired
};

function FilterChips({ t, relaxed }) {
  const results = allFilterResults(t);
  if (results.length === 0) return <span className="rv2-hint">no filter results</span>;
  return results.map((r) => {
    let variant = r.passed ? 'pass' : 'fail';
    let suffix = '';
    const isRelaxed = typeof r.relaxed === 'boolean' ? r.relaxed : relaxed.includes(r.filter);
    if (!r.passed && isRelaxed) {
      variant = 'warn';
      suffix = ' (relaxed)';
    }
    const title = `${r.filter} (Phase ${r.phase}): ${r.passed ? 'pass' : 'fail'}${suffix}${
      r.reason ? ` — ${r.reason}` : ''
    }`;
    return (
      <span
        key={`${r.phase}-${r.filter}`}
        className={`rv2-badge rv2-badge-${variant}`}
        title={title}
      >
        {r.passed ? '✓' : '✗'} {filterLabel(r.filter)}
        {suffix}
      </span>
    );
  });
}

FilterChips.propTypes = {
  t: teacherEvalShape.isRequired,
  relaxed: PropTypes.arrayOf(PropTypes.string).isRequired
};

const phaseCell = (phase) => {
  if (!phase) return <span className="rv2-hint">not run</span>;
  return (
    <span className={`rv2-badge rv2-badge-${phase.passed ? 'pass' : 'fail'}`}>
      {phase.passed ? 'pass' : 'fail'}
    </span>
  );
};

export function PoolTable({ pool, relaxed }) {
  const [view, setView] = useState('all');
  const [search, setSearch] = useState('');

  const failedCount = useMemo(() => pool.filter(teacherFailed).length, [pool]);
  const eligibleCount = pool.length - failedCount;
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pool.filter((t) => {
      if (view === 'eligible' && teacherFailed(t)) return false;
      if (view === 'excluded' && !teacherFailed(t)) return false;
      if (!q) return true;
      return `${t.name || ''} ${t.teacher_id}`.toLowerCase().includes(q);
    });
  }, [pool, view, search]);
  const views = [
    {
      key: 'all',
      label: `All (${pool.length})`,
      hint: 'Every approved teacher that was evaluated'
    },
    {
      key: 'eligible',
      label: `Eligible (${eligibleCount})`,
      hint: 'Passed every filter and competed in the ranking'
    },
    {
      key: 'excluded',
      label: `Excluded (${failedCount})`,
      hint: 'Removed by at least one filter; see the red chips for which'
    }
  ];

  return (
    <div className="rv2-card">
      <h3>
        Pool ({pool.length} teachers, {failedCount} failed)
      </h3>
      <div className="rv2-pool-summary">
        <strong>{eligibleCount} eligible</strong> competed in the ranking;{' '}
        <strong>{failedCount} excluded</strong> by a filter
        <span className="rv2-hint">
          {' '}
          · hover a chip for the filter key and the server&apos;s reason
        </span>
      </div>
      <div className="rv2-actions">
        <div className="rv2-seg" role="group" aria-label="Which teachers to list">
          <span className="rv2-hint">Show:</span>
          {views.map((v) => (
            <button
              key={v.key}
              type="button"
              title={v.hint}
              className={`rv2-seg-btn${view === v.key ? ' rv2-seg-active' : ''}`}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="rv2-field">
          <input
            aria-label="Search pool by teacher name or id"
            placeholder="Search name or id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="rv2-hint">
          Showing {rows.length} of {pool.length}
        </span>
      </div>
      {pool.length === 0 ? (
        <div className="rv2-empty">The pool is empty: no APPROVED teachers were evaluated.</div>
      ) : (
        <div className="rv2-table-wrap">
          <table className="table-st">
            <thead>
              <tr>
                <th title="Teacher name and id">Teacher</th>
                <th title="Phase A eligibility (opted in, ops removal, points, health, prenatal, test user). Never relaxed.">
                  Phase A
                </th>
                <th title="Phase B: slot availability (hard) and soft gender / western-comfort filters. Not run in Phase A only mode or when Phase A failed.">
                  Phase B
                </th>
                <th title="Final rank if the teacher survived Stage 0">Rank</th>
                <th title="Per-filter result. Red = failed, amber = failed but the filter was relaxed for the thin pool. Hover for the raw filter key and reason.">
                  Filters
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.teacher_id} className={teacherFailed(t) ? 'rv2-row-failed' : ''}>
                  <td>
                    <TeacherCell t={t} />
                  </td>
                  <td>{phaseCell(t.phase_a)}</td>
                  <td>{phaseCell(t.phase_b)}</td>
                  <td className="rv2-num">{t.rank || '—'}</td>
                  <td>
                    <FilterChips t={t} relaxed={relaxed} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

PoolTable.propTypes = {
  pool: PropTypes.arrayOf(teacherEvalShape).isRequired,
  relaxed: PropTypes.arrayOf(PropTypes.string)
};

PoolTable.defaultProps = { relaxed: [] };
